import React, { useState, useEffect } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../services/dashboardAPI';
import {
  SubscriptionCard,
  AppointmentCalendar,
  PlanDocuments,
  MessageCoach,
  PhotoGallery,
  WeeklyCheckInForm,
  CheckInHistory,
  DashboardStats,
} from '../components/dashboard';
import images from '../assets/images';

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const data = await dashboardAPI.getDashboardSummary();
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extract plan type from subscription or active plan name
  const getPlanType = () => {
    const planId = (dashboardData?.subscription?.planId || dashboardData?.activePlan?.plan_name || '').toLowerCase();
    if (planId.includes('glp1') || planId.includes('glp-1')) return 'GLP1';
    if (planId.includes('medical')) return 'MEDICAL';
    return 'CORE';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="relative bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white mb-8 shadow-lg overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={images.hero.main}
              alt="Welcome"
              className="w-full h-full object-cover opacity-20"
            />
          </div>
          <div className="relative z-10">
            <h1 className="text-3xl font-bold mb-2">
              Welcome back, {user?.firstName}!
            </h1>
            <p className="text-primary-100 text-lg">
              {dashboardData?.hasActiveSubscription
                ? "Let's continue your transformation journey"
                : "Subscribe to a plan to start your journey"}
            </p>
          </div>
        </div>

        {/* Subscription Card */}
        <SubscriptionCard subscription={dashboardData?.subscription} />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <DashboardStats data={dashboardData} />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-8">
            {/* Weekly Check-in Form */}
            <WeeklyCheckInForm
              currentWeek={dashboardData?.currentWeek || 1}
              onSuccess={loadDashboardData}
              existingData={dashboardData?.latestCheckIn}
              planType={getPlanType()}
            />

            {/* Photo Gallery */}
            <PhotoGallery onUploadClick={() => toast.info('Photo upload coming soon!')} />

            {/* Check-in History */}
            <CheckInHistory />
          </div>

          {/* Right Column - 1/3 width */}
          <div className="space-y-8">
            {/* Plan Documents */}
            <PlanDocuments documents={dashboardData?.availableDocuments} />

            {/* Appointment Calendar */}
            <AppointmentCalendar calendlyUrl={dashboardData?.calendlyUrl} />

            {/* Message Coach */}
            <MessageCoach phoneNumber={dashboardData?.whatsappNumber} />

            {/* Quick Tips */}
            <div className="bg-gradient-to-br from-success-50 to-success-100 rounded-2xl p-6 border border-success-200">
              <h3 className="font-bold text-gray-900 mb-3">Quick Tips</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-success-600 font-bold">1.</span>
                  Submit your weekly check-in every Sunday
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success-600 font-bold">2.</span>
                  Upload progress photos for visual tracking
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success-600 font-bold">3.</span>
                  Download your meal & workout plans
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success-600 font-bold">4.</span>
                  Message your coach anytime for support
                </li>
              </ul>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
};

export default Dashboard;
